import axios from 'axios';

export interface IProfaseArchivoUsuario {
  id?: number;
  nombre?: string;
  apellido?: string;
  email?: string;
}

export interface IProfaseArchivoData {
  subidoPor?: IProfaseArchivoUsuario;
  /** ISO-8601 (Instant) */
  subido?: string;
}

export interface IProfaseArchivo {
  id?: number;
  nombre: string;
  path: string;
  contenidoUuid?: string | null;
  esDirectorio: boolean;
  archivoPadre?: Pick<IProfaseArchivo, 'id'> | null;
  data?: IProfaseArchivoData | null;
}

/** Etiqueta legible del usuario que subió el archivo (ProfaseArchivo.MD). */
export function labelSubidoPor(data?: IProfaseArchivoData | null): string {
  const u = data?.subidoPor;
  if (!u) return '';
  const nombre = [u.nombre, u.apellido].filter(Boolean).join(' ').trim();
  return nombre || u.email?.trim() || '';
}

export function formatProfaseArchivoSubido(iso?: string | null): string {
  if (!iso?.trim()) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' });
}

/** Texto `(subidoPor, fecha)` para títulos de visualizadores. */
export function formatArchivoSubidoMeta(data?: IProfaseArchivoData | null): string {
  const subidoPor = labelSubidoPor(data);
  const fecha = formatProfaseArchivoSubido(data?.subido);
  if (subidoPor && fecha) return `(${subidoPor}, ${fecha})`;
  if (subidoPor) return `(${subidoPor})`;
  if (fecha) return `(${fecha})`;
  return '';
}

function parseContentDispositionFilename(contentDisposition: unknown, fallback: string): string {
  let filename = fallback.trim() || 'archivo';
  if (typeof contentDisposition !== 'string') {
    return filename;
  }

  const star = /filename\*=UTF-8''([^;\s]+)/i.exec(contentDisposition);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].replace(/"/g, ''));
    } catch {
      return star[1];
    }
  }

  const quoted = /filename="([^"]+)"/i.exec(contentDisposition);
  const plain = quoted ?? /filename=([^;\s]+)/i.exec(contentDisposition);
  if (plain?.[1]) {
    return plain[1].replace(/"/g, '');
  }

  return filename;
}

export function joinProfaseArchivoPath(parentPath: string, fileName: string): string {
  const trimmedParent = parentPath.trim().replace(/^\/+|\/+$/g, '');
  if (!trimmedParent) {
    return fileName;
  }
  return `${trimmedParent}/${fileName}`;
}

async function resolveProfaseArchivoFolderByPath(logicalPath: string): Promise<IProfaseArchivo | null> {
  const searchParams = new URLSearchParams();
  searchParams.append('path', logicalPath);
  searchParams.append('esDirectorio', 'true');
  searchParams.append('page', '0');
  searchParams.append('size', '50');

  const { data } = await axios.get<IProfaseArchivo[]>(`api/profase-archivos?${searchParams.toString()}`);
  return data.find(item => item.path === logicalPath) ?? null;
}

async function resolveUploadParentId(
  destinationPath: string,
  hints?: {
    listingParentId?: number;
    selectedFolder?: IProfaseArchivo | null;
  },
): Promise<number | null> {
  if (!destinationPath) {
    return null;
  }

  const selectedFolder = hints?.selectedFolder;
  if (
    selectedFolder?.esDirectorio
    && selectedFolder.path === destinationPath
    && selectedFolder.id != null
  ) {
    return selectedFolder.id;
  }

  if (hints?.listingParentId != null) {
    return hints.listingParentId;
  }

  const folder = await resolveProfaseArchivoFolderByPath(destinationPath);
  if (folder?.id == null) {
    throw new Error('No se encontró la carpeta destino.');
  }
  return folder.id;
}

/**
 * Crea metadatos (POST) y sube bytes (PUT …/contenido) en la carpeta indicada por `destinationPath`.
 * Raíz si `destinationPath` está vacío.
 */
export async function uploadProfaseArchivoToPath(
  destinationPath: string,
  file: File,
  hints?: {
    listingParentId?: number;
    selectedFolder?: IProfaseArchivo | null;
  },
): Promise<IProfaseArchivo> {
  const filePath = joinProfaseArchivoPath(destinationPath, file.name);
  const parentId = await resolveUploadParentId(destinationPath, hints);

  const { data: created } = await axios.post<IProfaseArchivo>('api/profase-archivos', {
    nombre: file.name,
    path: filePath,
    esDirectorio: false,
    ...(parentId != null ? { archivoPadre: { id: parentId } } : {}),
  });

  if (created.id == null) {
    throw new Error('No se pudo registrar el archivo.');
  }

  const formData = new FormData();
  formData.append('file', file, file.name);

  try {
    const { data: updated } = await axios.put<IProfaseArchivo>(
      `api/profase-archivos/${created.id}/contenido`,
      formData,
    );
    return updated ?? created;
  } catch (error) {
    try {
      await axios.delete(`api/profase-archivos/${created.id}`);
    } catch {
      // best-effort rollback
    }
    throw error;
  }
}

/** POST api/profase-archivos → crea carpeta en el directorio indicado por `destinationPath`. */
export async function createProfaseArchivoFolder(
  destinationPath: string,
  folderName: string,
  hints?: {
    listingParentId?: number;
    selectedFolder?: IProfaseArchivo | null;
  },
): Promise<IProfaseArchivo> {
  const trimmedName = folderName.trim();
  if (!trimmedName) {
    throw new Error('El nombre de la carpeta es obligatorio.');
  }
  if (trimmedName.includes('/')) {
    throw new Error('El nombre de la carpeta no puede contener "/".');
  }

  const folderPath = joinProfaseArchivoPath(destinationPath, trimmedName);
  const parentId = await resolveUploadParentId(destinationPath, hints);

  const { data } = await axios.post<IProfaseArchivo>('api/profase-archivos', {
    nombre: trimmedName,
    path: folderPath,
    esDirectorio: true,
    ...(parentId != null ? { archivoPadre: { id: parentId } } : {}),
  });

  return data;
}

/** GET api/profase-archivos/{id}/contenido → blob y nombre de archivo. */
export async function fetchProfaseArchivoContenido(
  archivoId: number,
  fallbackNombre: string,
): Promise<{ blob: Blob; filename: string }> {
  const url = `api/profase-archivos/${archivoId}/contenido`;
  const res = await axios.get(url, { responseType: 'blob' });
  const filename = parseContentDispositionFilename(res.headers['content-disposition'], fallbackNombre);
  return { blob: res.data as Blob, filename };
}

/** GET api/profase-archivos/{id}/contenido → descarga del blob en S3. */
export async function downloadProfaseArchivoContenido(archivo: IProfaseArchivo): Promise<void> {
  if (archivo.id == null) {
    throw new Error('El archivo no tiene id.');
  }

  const { blob, filename } = await fetchProfaseArchivoContenido(archivo.id, archivo.nombre);
  const href = URL.createObjectURL(blob);

  try {
    const a = document.createElement('a');
    a.href = href;
    a.download = filename;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(href);
  }
}

/** DELETE api/profase-archivos/{id} → elimina metadatos del archivo o carpeta. */
export async function deleteProfaseArchivo(archivoId: number): Promise<void> {
  await axios.delete(`api/profase-archivos/${archivoId}`);
}

/** GET api/profase-archivos/{id}/contenido → abre el blob en una nueva pestaña. */
export async function openProfaseArchivoContenido(
  archivoId: number,
  fallbackNombre: string,
): Promise<void> {
  const { blob } = await fetchProfaseArchivoContenido(archivoId, fallbackNombre);
  const href = URL.createObjectURL(blob);
  const win = window.open(href, '_blank', 'noopener,noreferrer');
  if (!win) {
    URL.revokeObjectURL(href);
    throw new Error('No se pudo abrir el archivo.');
  }
  setTimeout(() => URL.revokeObjectURL(href), 60_000);
}
